import { useEffect, useState } from 'react';
import { Truck, Loader2, Calendar, Hash, Package, MapPin, ExternalLink } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { purchasingAPI, AdvancedShippingNotice } from '../../lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useFormatters } from '../../contexts/PreferencesContext';

interface POLineASNTabProps {
  lineId: number;
}

interface ASNSummary {
  total_asns: number;
  total_shipped: string;
  asns: AdvancedShippingNotice[];
}

export default function POLineASNTab({ lineId }: POLineASNTabProps) {
  const [summary, setSummary] = useState<ASNSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchASNSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const fetchASNSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getLineASNs(lineId);
      setSummary({
        total_asns: response.data.total_asns,
        total_shipped: response.data.total_shipped,
        asns: response.data.asns,
      });
    } catch (err: unknown) {
      console.error('Failed to fetch ASN summary:', err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || 'Failed to load ASN summary');
      toast.error('Failed to load ASN summary');
    } finally {
      setLoading(false);
    }
  };

  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string; icon: LucideIcon }> = {
      created: { label: 'Created', className: 'bg-gray-100 text-gray-800 border-gray-200', icon: Package },
      submitted: { label: 'Submitted', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Truck },
      in_transit: { label: 'In Transit', className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Truck },
      delivered: { label: 'Delivered', className: 'bg-green-100 text-green-800 border-green-200', icon: MapPin },
      received: { label: 'Received', className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: Package },
      cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800 border-red-200', icon: Package },
    };

    const config = statusConfig[status] || statusConfig['created'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const getCourierLabel = (courier: string | undefined) => {
    const courierLabels: Record<string, string> = {
      fedex: 'FedEx',
      ups: 'UPS',
      dhl: 'DHL',
      tnt: 'TNT',
      dpd: 'DPD',
      other: 'Other',
    };
    return courierLabels[courier || ''] || courier || '-';
  };

  const getTransportLabel = (method: string | undefined) => {
    const transportLabels: Record<string, string> = {
      road: 'Road',
      rail: 'Rail',
      air: 'Air',
      sea: 'Sea',
    };
    return transportLabels[method || ''] || method || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Truck className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchASNSummary}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!summary || summary.total_asns === 0) {
    return (
      <div className="text-center py-12">
        <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground font-medium">No shipments found for this PO line</p>
        <p className="text-sm text-muted-foreground mt-2">
          Advanced Shipping Notices will appear here once shipments are created
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Total Shipments</span>
            <Truck className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-900">{summary.total_asns}</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-900">Total Qty Shipped</span>
            <Package className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">{parseFloat(summary.total_shipped).toLocaleString()}</p>
        </div>
      </div>

      {/* ASN List */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4">Shipment History</h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">ASN Number</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Courier</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Transport</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Tracking</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Ship Date</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Est. Delivery</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {summary.asns.map((asn, index) => (
                <tr
                  key={asn.id}
                  className={`hover:bg-muted/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-primary">{asn.asn_number}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(asn.status)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{getCourierLabel(asn.courier)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{getTransportLabel(asn.method_of_transport)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                      {asn.tracking_number || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{formatDate(asn.shipment_date)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{formatDate(asn.estimated_delivery_date)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/purchasing?tab=asn&asn=${asn.id}`}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      View
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Carbon Emissions Summary (if available) */}
      {summary.asns.some(asn => asn.carbon_emission_kg) && (
        <div className="bg-muted/30 border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-muted-foreground">Environmental Impact</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Total Carbon Emissions</p>
              <p className="text-xl font-bold text-primary">
                {summary.asns.reduce((sum, asn) => sum + parseFloat(asn.carbon_emission_kg || '0'), 0).toFixed(2)} kg CO₂
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Total Weight Shipped</p>
              <p className="text-xl font-bold text-primary">
                {summary.asns.reduce((sum, asn) => sum + parseFloat(asn.weight_kg || '0'), 0).toFixed(2)} kg
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
